import type { AuthUser } from '../types'

interface AuthTokenPayload {
    token: string
    user: AuthUser
    initData: Record<string, unknown>
    expiresIn: number
}

export type TabSyncEvent =
    | ({ type: 'AUTH_LOGIN' } & AuthTokenPayload)
    | { type: 'AUTH_LOGOUT' }
    | { type: 'AUTH_SESSION_EXPIRED' }
    | ({ type: 'AUTH_SESSION_EXTENDED' } & AuthTokenPayload)

type TabSyncListener = (event: TabSyncEvent) => void

const CHANNEL_NAME = 'sfy_cms_auth'
const STORAGE_KEY = 'sfy_cms_auth_sync'

class TabSyncManager {
    private channel: BroadcastChannel | null = null
    private listeners = new Set<TabSyncListener>()
    private tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    private initialized = false

    initialize(): void {
        if (this.initialized) return
        this.initialized = true

        try {
            this.channel = new BroadcastChannel(CHANNEL_NAME)
            this.channel.onmessage = (
                event: MessageEvent<TabSyncEvent & { _tabId: string }>
            ) => {
                if (event.data._tabId === this.tabId) return
                this.notifyListeners(event.data)
            }
        } catch {
            window.addEventListener('storage', this.handleStorageEvent)
        }
    }

    broadcast(event: TabSyncEvent): void {
        if (this.channel) {
            this.channel.postMessage({ ...event, _tabId: this.tabId })
        } else {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({ ...event, _ts: Date.now() })
            )
        }
    }

    subscribe(listener: TabSyncListener): () => void {
        this.listeners.add(listener)
        return () => this.listeners.delete(listener)
    }

    destroy(): void {
        this.channel?.close()
        this.channel = null
        this.initialized = false
        window.removeEventListener('storage', this.handleStorageEvent)
        this.listeners.clear()
    }

    private notifyListeners(event: TabSyncEvent): void {
        this.listeners.forEach((listener) => listener(event))
    }

    private handleStorageEvent = (e: StorageEvent): void => {
        if (e.key !== STORAGE_KEY || !e.newValue) return
        try {
            const event = JSON.parse(e.newValue) as TabSyncEvent
            this.notifyListeners(event)
        } catch {
            // Malformed event — ignore
        }
    }
}

export const tabSync = new TabSyncManager()
